import { useNavigate, useParams } from "@tanstack/react-router";
import { Skeleton } from "@/components/ui/skeleton";
import { trpcClient } from "@/lib/trpc";
import PurchasingForm, { type PurchasingFormData } from "./PurchasingForm";
import { toast } from "sonner";
import { useQuery } from "@tanstack/react-query";
import React from "react";
import StandardDetailLayout from "@/layouts/StandardDetailLayout";

import { toVnDateString } from "@apv-erp/status";
export default function PurchasingEdit() {
  const { poId } = useParams({ strict: false }) as { poId: string };
  const navigate = useNavigate();

  const { data: po, isLoading } = useQuery({
    queryKey: ["purchase-order", poId],
    queryFn: () => trpcClient.purchaseOrders.getById.query({ id: poId }),
  });

  const handleSubmit = async (data: any) => {
    try {
      await trpcClient.purchaseOrders.update.mutate({ id: poId, data });
      toast.success("Cập nhật đơn mua hàng thành công!");
      navigate({ to: "/purchasing" });
    } catch (e: any) {
      toast.error(e.message || "Cập nhật đơn mua vật tư thất bại");
    }
  };

  if (isLoading)
    return (
      <div className="w-full space-y-4 p-4">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  if (!po)
    return (
      <div className="p-4 text-center text-rose-500">
        Không tìm thấy đơn hàng
      </div>
    );

  // Transform backend data to form defaultValues
  const defaultValues: Partial<PurchasingFormData> = {
    id: po.id,
    vendorId: po.vendorId,
    orderDate: po.orderDate ? toVnDateString(po.orderDate) : "",
    expectedDate: po.expectedDate
      ? toVnDateString(po.expectedDate)
      : "",
    receivedDate: po.receivedDate
      ? toVnDateString(po.receivedDate)
      : "",
    notes: po.notes || "",
    status: po.status,
    items: po.items?.map((item: any) => {
      // Dòng mua theo ĐVT phụ: form hiện lại số đã gõ (entry*) và ĐVT đó (04/11/2026)
      const byEntry = !!item.entryUnit && item.entryQty != null;
      const quantity = byEntry ? Number(item.entryQty) : Number(item.quantity);
      const unitPrice = byEntry && item.entryUnitPrice != null ? Number(item.entryUnitPrice) : Number(item.unitPrice);
      return {
        id: item.id,
        notes: item.notes || "",
        materialId: item.materialId,
        productionOrderId: item.productionOrderId || undefined,
        quantity,
        unitPrice,
        totalAmount: byEntry ? quantity * unitPrice : Number(item.quantity) * Number(item.unitPrice),
        materialPlanId: item.materialPlanningId || undefined,
        entryUnit: byEntry ? item.entryUnit : null,
        unitMeta: item.material
          ? { unit: item.material.unit, secondaryUnit: item.material.secondaryUnit, secondaryFactor: item.material.secondaryFactor }
          : null,
      };
    }) || [{ materialId: "", quantity: 1, unitPrice: 0, totalAmount: 0 }],
  };

  // Keep original vendor data for lookup display
  (defaultValues as any).vendor = po.vendor;
  if (po.items) {
    (defaultValues as any).items = defaultValues.items?.map((item, idx) => ({
      ...item,
      materialName: po.items[idx].material?.name,
      lsxId: po.items[idx].productionOrder?.orderId,
    }));
  }

  return (
    <StandardDetailLayout
      breadcrumbs={[
        { label: "Đơn Mua Hàng", to: "/purchasing" },
        { label: po.poId, to: `/purchasing/${po.id}` },
        { label: "Chỉnh sửa" },
      ]}
      title={`Sửa ${po.poId}`}
      onBack={() => navigate({ to: "/purchasing" })}
      showContextPanel={false}
    >
      <div className="w-full space-y-4 p-4 animate-in fade-in duration-300">
        <PurchasingForm
          defaultValues={defaultValues}
          onSubmit={handleSubmit}
          onCancel={() => navigate({ to: "/purchasing" })}
          submitLabel={(po as any)?.status === "DRAFT" ? "Lưu" : "Cập nhật"}
        />
      </div>
    </StandardDetailLayout>
  );
}
